import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from '../../axios/axiosconf';
import { descargarPresupuestoPdf } from './presupuestoPdfUtils';

function VistaPreviaPresupuesto() {
    const { id } = useParams();
    const [presupuesto, setPresupuesto] = useState(null);
    const [client, setClient] = useState(null);
    const [paciente, setPaciente] = useState(null);
    const [usuarios, setUsuarios] = useState([]);
    const [encabezadoPersonalizado, setEncabezadoPersonalizado] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        cargarDatos();
    }, [id]);

    const cargarDatos = async () => {
        let userdata = JSON.parse(localStorage.getItem('userdata') || '{}');

        try {
            const [clienteResponse, presupuestoResponse, pacienteResponse, usuariosResponse] = await Promise.all([
                axios.get(`/get-client?uid=${userdata.user_id}`),
                axios.get(`/presupuesto/${id}?uid=${userdata.user_id}`),
                axios.get(`/pacientes?uid=${userdata.user_id}`),
                axios.get(`/users-by-client?uid=${userdata.user_id}`)
            ]);

            setClient(clienteResponse.data);
            setPresupuesto(presupuestoResponse.data);
            setUsuarios(usuariosResponse.data);

            let pacienteEncontrado = pacienteResponse.data.find(
                (p) => String(p.id) === String(presupuestoResponse.data.paciente_id)
            );
            if (!pacienteEncontrado?.numero_documento && presupuestoResponse.data.paciente_id) {
                try {
                    const pacienteDetalle = await axios.get(
                        `/paciente?uid=${userdata.user_id}&id=${presupuestoResponse.data.paciente_id}`
                    );
                    if (pacienteDetalle.data?.[0]) {
                        pacienteEncontrado = pacienteDetalle.data[0];
                    }
                } catch (error) {
                    console.log('No se pudo cargar el detalle del paciente:', error);
                }
            }
            setPaciente(pacienteEncontrado);

            // Cargar encabezado personalizado según el users_id del presupuesto
            if (presupuestoResponse.data.users_id) {
                try {
                    const encabezadoResponse = await axios.get(`/encabezado-personalizado?uid=${presupuestoResponse.data.users_id}`);
                    if (encabezadoResponse.status === 200 && encabezadoResponse.data && encabezadoResponse.data.estado === 1) {
                        setEncabezadoPersonalizado(encabezadoResponse.data); // Guardar el objeto completo
                        console.log('Encabezado personalizado cargado:', encabezadoResponse.data);
                    }
                } catch (error) {
                    console.log('No hay encabezado personalizado para este usuario');
                }
            }

        } catch (error) {
            console.error('Error al cargar datos:', error);
            alert('Error al cargar los datos del presupuesto');
        } finally {
            setLoading(false);
        }
    };

    const imprimir = () => {
        window.print();
    };

    const descargarPDF = async () => {
        try {
            await descargarPresupuestoPdf({
                presupuesto,
                client,
                paciente,
                encabezadoPersonalizado,
                usuario: usuarios.find((u) => u.id === presupuesto.users_id),
            });
        } catch (error) {
            console.error('Error generando PDF:', error);
            alert('Error al generar el PDF. Por favor, intente nuevamente.');
        }
    };

    const getNombreUsuario = (usuarioId) => {
        const usuario = usuarios.find(u => u.id === usuarioId);
        return usuario ? usuario.name : 'N/A';
    };

    const generarEncabezadoHTML = () => {
        if (!encabezadoPersonalizado) {
            // Encabezado por defecto si no hay personalizado
            return `
                <div style="margin-bottom: 20px;">
                    <div style="display: flex; align-items: center; justify-content: space-between;">
                        <div style="flex: 0 0 25%; text-align: center;">
                            ${client?.logo ? `<img src="${client.logo}" style="max-width: 150px; max-height: 80px;" />` : ''}
                        </div>
                        <div style="flex: 1; text-align: center;">
                            <h4 style="margin-bottom: 5px;">${client?.nombre || ''}</h4>
                            <address style="margin: 0; font-size: 12px;">
                                ${client?.direccion || ''}<br />
                                ${client?.telefono || ''}<br />
                                Email: ${client?.email || ''}
                            </address>
                        </div>
                        <div style="flex: 0 0 25%;"></div>
                    </div>
                </div>
            `;
        }

        // Función auxiliar para limpiar HTML y evitar saltos de línea innecesarios
        const limpiarHTML = (html) => {
            if (!html) return '';
            return html
                .replace(/<p>/gi, '<div style="margin: 0; padding: 0; line-height: 1.1;">')
                .replace(/<\/p>/gi, '</div>')
                .replace(/<br\s*\/?>/gi, '<br style="line-height: 1.1;" />');
        };

        // USAR TABLA EN LUGAR DE FLEXBOX PARA MEJOR COMPATIBILIDAD CON PDF
        return `
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; background-color: #ffffff;">
                <tr>
                    <!-- Columna Izquierda (30%) -->
                    <td style="width: 30%; padding: 2px; font-size: 10px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.izquierda)}
                    </td>
                    
                    <!-- Columna Centro (40%) -->
                    <td style="width: 40%; padding: 2px; text-align: center; font-size: 11px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.centro)}
                    </td>
                    
                    <!-- Columna Derecha (30%) -->
                    <td style="width: 30%; padding: 2px; text-align: right; font-size: 10px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.derecha)}
                    </td>
                </tr>
            </table>
        `;
    };

    const enviarWhatsApp = async () => {
        const telefonoPaciente = paciente.celular || paciente.telefono;
        if (!telefonoPaciente) {
            alert('El paciente no tiene teléfono registrado');
            return;
        }

        const numeroWhatsApp = telefonoPaciente.startsWith('+57') ? telefonoPaciente : `+57${telefonoPaciente}`;
        const mensaje = `Hola ${nombrePaciente}, te enviamos tu presupuesto N° ${presupuesto.numero_presupuesto} por valor de ${Math.round(parseFloat(presupuesto.valor_total)).toLocaleString('es-CO')}. Válido hasta: ${new Date(presupuesto.fecha_vence).toLocaleDateString()}.`;

        try {
            const url = `https://wa.me/${numeroWhatsApp}?text=${encodeURIComponent(mensaje)}`;
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error enviando WhatsApp:', error);
            alert('Error al abrir WhatsApp');
        }
    };

    if (loading || !presupuesto || !client || !paciente) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Cargando...</span>
                    </div>
                    <p className="mt-2">Cargando presupuesto...</p>
                </div>
            </div>
        );
    }

    const nombrePaciente = `${paciente.primer_nombre || ''} ${paciente.segundo_nombre || ''} ${paciente.primer_apellido || ''} ${paciente.segundo_apellido || ''}`.trim();
    const nombreUsuario = getNombreUsuario(presupuesto.users_id);
    const totalAjuste = presupuesto.detalle?.reduce((acc, item) => {
        return acc + (parseFloat(item.ajuste) || 0);
    }, 0) || 0;

    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        const date = new Date(fecha);
        const dia = String(date.getDate()).padStart(2, '0');
        const mes = String(date.getMonth() + 1).padStart(2, '0');
        const anio = String(date.getFullYear()).slice(-2);
        return `${dia}/${mes}/${anio}`;
    };

    const construirDireccionCompleta = () => {
        const partes = [];
        if (paciente.direccion) partes.push(paciente.direccion);
        if (paciente.zona) partes.push(paciente.zona);
        if (paciente.nombre_ciudad_residencia) partes.push(paciente.nombre_ciudad_residencia);
        if (paciente.nombre_departamento_residencia) partes.push(paciente.nombre_departamento_residencia);
        if (paciente.nombre_pais_residencia) partes.push(paciente.nombre_pais_residencia);
        return partes.join(', ') || paciente.direccion || '';
    };

    const obtenerDocumento = () => {
        if (!paciente) return '';
        return paciente.numero_documento || paciente.numeroDocumento || '';
    };

    const mostrarDescripcion = presupuesto.detalle?.some(item => String(item.descripcion || '').trim() !== '');
    const colspanTotales = mostrarDescripcion ? 7 : 6;

    return (
        <div className="container-fluid p-4">
            <style>{`
                @media print {
                    .no-print {
                        display: none !important;
                    }
                    body, html, #root {
                        margin: 0;
                        padding: 0;
                        background: #fff;
                    }
                    .container-fluid {
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    .invoice {
                        width: 100% !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    .invoice .table-responsive {
                        overflow: visible !important;
                    }
                    .invoice table {
                        width: 100% !important;
                        table-layout: fixed;
                    }
                }
            `}</style>

            {/* Botones arriba */}
            <div className="no-print mb-3 text-center">
                <button onClick={imprimir} className="btn btn-dark btn-lg mr-2">
                    <i className="fas fa-print mr-2"></i>
                    Imprimir
                </button>
                <button onClick={descargarPDF} className="btn btn-danger btn-lg">
                    <i className="fas fa-file-pdf mr-2"></i>
                    Descargar PDF
                </button>
            </div>

            <div className="invoice p-3 mb-3">
                {/* Encabezado Personalizado */}
                <div dangerouslySetInnerHTML={{ __html: generarEncabezadoHTML() }} />

                {/* Banner: PRESUPUESTO */}
                <div style={{ marginBottom: '10px' }}>
                    <div style={{ backgroundColor: '#e9ecef', padding: '8px 12px', fontWeight: 'bold', fontSize: '13px', borderRadius: '3px', textAlign: 'center' }}>
                        PRESUPUESTO N° {presupuesto.numero_presupuesto}
                    </div>
                </div>

                {/* Datos del Presupuesto */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px', fontSize: '11px' }}>
                    <tbody>
                        <tr>
                            <td style={{ backgroundColor: '#e9ecef', fontWeight: 'bold', width: '15%', verticalAlign: 'middle', padding: '6px', border: '1px solid #dee2e6' }}>FECHA CREACIÓN</td>
                            <td style={{ backgroundColor: '#ffffff', width: '18%', padding: '6px', border: '1px solid #dee2e6' }}>{formatearFecha(presupuesto.fecha_creacion)}</td>
                            <td style={{ backgroundColor: '#e9ecef', fontWeight: 'bold', width: '15%', verticalAlign: 'middle', padding: '6px', border: '1px solid #dee2e6' }}>VÁLIDO HASTA</td>
                            <td style={{ backgroundColor: '#ffffff', width: '18%', padding: '6px', border: '1px solid #dee2e6' }}>{formatearFecha(presupuesto.fecha_vence)}</td>
                            <td style={{ backgroundColor: '#e9ecef', fontWeight: 'bold', width: '15%', verticalAlign: 'middle', padding: '6px', border: '1px solid #dee2e6' }}>FORMA DE PAGO</td>
                            <td style={{ backgroundColor: '#ffffff', width: '19%', padding: '6px', border: '1px solid #dee2e6' }}>{presupuesto.forma_pago}</td>
                        </tr>
                        <tr>
                            <td style={{ backgroundColor: '#e9ecef', fontWeight: 'bold', verticalAlign: 'middle', padding: '6px', border: '1px solid #dee2e6' }}>CREADO POR</td>
                            <td colSpan="5" style={{ backgroundColor: '#ffffff', padding: '6px', border: '1px solid #dee2e6' }}>{nombreUsuario}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Datos del Paciente */}
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#f8f9fa', fontSize: '12px', marginBottom: '20px' }}>
                    <tbody>
                        <tr>
                            <td style={{ backgroundColor: '#e9ecef', fontWeight: 'bold', width: '15%', verticalAlign: 'middle', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>PACIENTE</td>
                            <td style={{ backgroundColor: '#ffffff', width: '35%', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>{nombrePaciente}</td>
                            <td style={{ backgroundColor: '#e9ecef', fontWeight: 'bold', width: '15%', verticalAlign: 'middle', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>DOCUMENTO</td>
                            <td style={{ backgroundColor: '#ffffff', width: '35%', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>{obtenerDocumento()}</td>
                        </tr>
                        <tr>
                            <td style={{ backgroundColor: '#e9ecef', fontWeight: 'bold', verticalAlign: 'middle', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>TELÉFONO</td>
                            <td style={{ backgroundColor: '#ffffff', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>{paciente.telefono || ''}</td>
                            <td style={{ backgroundColor: '#e9ecef', fontWeight: 'bold', verticalAlign: 'middle', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>CELULAR</td>
                            <td style={{ backgroundColor: '#ffffff', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>{paciente.celular || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ backgroundColor: '#e9ecef', fontWeight: 'bold', verticalAlign: 'middle', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>EMAIL</td>
                            <td style={{ backgroundColor: '#ffffff', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>{paciente.email || ''}</td>
                            <td style={{ backgroundColor: '#e9ecef', fontWeight: 'bold', verticalAlign: 'middle', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>DIRECCIÓN</td>
                            <td style={{ backgroundColor: '#ffffff', padding: '6px', fontSize: '11px', border: '1px solid #dee2e6' }}>{construirDireccionCompleta()}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Tabla de Servicios */}
                <div className="table-responsive">
                    <table className="table table-hover" style={{ marginBottom: '20px', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#e9ecef' }}>
                                <th style={{ borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', padding: '8px', fontSize: '10px' }}>Código</th>
                                <th style={{ borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', padding: '8px', fontSize: '10px' }}>Servicio</th>
                                {mostrarDescripcion && (
                                    <th style={{ borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', padding: '8px', fontSize: '10px' }}>Descripción</th>
                                )}
                                <th style={{ borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', padding: '8px', textAlign: 'center', fontSize: '11px' }}>Cantidad</th>
                                <th style={{ borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', padding: '8px', textAlign: 'right', fontSize: '11px' }}>Precio Unit.</th>
                                <th style={{ borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', padding: '8px', textAlign: 'center', fontSize: '11px' }}>Descuento</th>
                                <th style={{ borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', padding: '8px', textAlign: 'center', fontSize: '11px' }}>Impuesto</th>
                                <th style={{ borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', padding: '8px', textAlign: 'right', fontSize: '11px' }}>Ajuste</th>
                                <th style={{ borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', padding: '8px', textAlign: 'right', fontSize: '11px' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {presupuesto.detalle?.map((item, index) => (
                                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff' }}>
                                    <td style={{ borderBottom: '1px solid #eef0f2', padding: '8px', fontSize: '10px' }}>{item.lista_precio?.codigo}</td>
                                    <td style={{ borderBottom: '1px solid #eef0f2', padding: '8px', fontSize: '10px' }}>{item.servicio || item.lista_precio?.nombre}</td>
                                    {mostrarDescripcion && (
                                        <td style={{ borderBottom: '1px solid #eef0f2', padding: '8px', fontSize: '10px' }}>{item.descripcion || ''}</td>
                                    )}
                                    <td style={{ borderBottom: '1px solid #eef0f2', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.cantidad}</td>
                                    <td style={{ borderBottom: '1px solid #eef0f2', padding: '8px', textAlign: 'right', fontSize: '11px' }}>${Math.round(parseFloat(item.precio_unitario)).toLocaleString('es-CO')}</td>
                                    <td style={{ borderBottom: '1px solid #eef0f2', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.porcentaje}%</td>
                                    <td style={{ borderBottom: '1px solid #eef0f2', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.impuesto}%</td>
                                    <td style={{ borderBottom: '1px solid #eef0f2', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
                                        ${Math.round(parseFloat(item.ajuste) || 0).toLocaleString('es-CO')}
                                    </td>
                                    <td style={{ borderBottom: '1px solid #eef0f2', padding: '8px', textAlign: 'right', fontSize: '11px', fontWeight: 'bold' }}>${Math.round(parseFloat(item.total)).toLocaleString('es-CO')}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                                <td style={{ textAlign: 'left', padding: '8px', backgroundColor: '#e9ecef', fontWeight: 'bold', fontSize: '11px', paddingLeft: '8px' }}>Subtotal:</td>
                                <td style={{ textAlign: 'right', padding: '8px', backgroundColor: '#e9ecef', fontSize: '11px' }}>${Math.round(parseFloat(presupuesto.valor_bruto)).toLocaleString('es-CO')}</td>
                            </tr>
                            <tr>
                                <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                                <td style={{ textAlign: 'left', padding: '8px', backgroundColor: '#e9ecef', fontWeight: 'bold', color: '#dc3545', fontSize: '11px', paddingLeft: '8px' }}>Descuento:</td>
                                <td style={{ textAlign: 'right', padding: '8px', backgroundColor: '#e9ecef', color: '#dc3545', fontSize: '11px' }}>
                                    -${Math.round(presupuesto.detalle?.reduce((acc, item) => {
                                        const precioBase = parseFloat(item.precio_unitario) * parseFloat(item.cantidad);
                                        return acc + (precioBase * (parseFloat(item.porcentaje) / 100));
                                    }, 0) || 0).toLocaleString('es-CO')}
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                                <td style={{ textAlign: 'left', padding: '8px', backgroundColor: '#e9ecef', fontWeight: 'bold', fontSize: '11px', paddingLeft: '8px' }}>Impuesto:</td>
                                <td style={{ textAlign: 'right', padding: '8px', backgroundColor: '#e9ecef', fontSize: '11px' }}>
                                    ${Math.round(presupuesto.detalle?.reduce((acc, item) => {
                                        const precioBase = parseFloat(item.precio_unitario) * parseFloat(item.cantidad);
                                        const descuento = precioBase * (parseFloat(item.porcentaje) / 100);
                                        const baseConDescuento = precioBase - descuento;
                                        return acc + (baseConDescuento * (parseFloat(item.impuesto) / 100));
                                    }, 0) || 0).toLocaleString('es-CO')}
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                                <td style={{ textAlign: 'left', padding: '8px', backgroundColor: '#e9ecef', fontWeight: 'bold', fontSize: '11px', paddingLeft: '8px' }}>Ajuste:</td>
                                <td style={{ textAlign: 'right', padding: '8px', backgroundColor: '#e9ecef', fontSize: '11px' }}>
                                    ${Math.round(totalAjuste).toLocaleString('es-CO')}
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                                <td style={{ textAlign: 'left', padding: '8px', backgroundColor: '#e9ecef', fontWeight: 'bold', fontSize: '12px', paddingLeft: '8px' }}>Total:</td>
                                <td style={{ textAlign: 'right', padding: '8px', backgroundColor: '#e9ecef', fontSize: '12px', fontWeight: 'bold' }}>${Math.round(parseFloat(presupuesto.valor_total)).toLocaleString('es-CO')}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Observaciones */}
                {presupuesto.observaciones && (
                    <div style={{ marginTop: '20px' }}>
                        <h6 style={{ fontSize: '14px', fontWeight: 'bold' }}>Observaciones:</h6>
                        <p style={{ fontSize: '12px' }}>{presupuesto.observaciones}</p>
                    </div>
                )}

                {/* Nota Informativa */}
                <div style={{ marginTop: '30px', padding: '12px', backgroundColor: '#d1ecf1', border: '1px solid #bee5eb', borderRadius: '4px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#0c5460' }}>
                        <i className="fas fa-info-circle"></i>
                        <strong> Nota:</strong> Este es un presupuesto válido hasta la fecha indicada. Los precios pueden estar sujetos a cambios.
                    </p>
                </div>
            </div>
        </div >
    );
}

export default VistaPreviaPresupuesto;